import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Alert, Button, TextField, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/auth.service';
import QRCode from 'qrcode';

export const SettingsPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [setupData, setSetupData] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const buildQr = async () => {
      if (!setupData?.otpauth_url) {
        setQrDataUrl(null);
        return;
      }
      try {
        const url = await QRCode.toDataURL(setupData.otpauth_url);
        if (isMounted) {
          setQrDataUrl(url);
        }
      } catch (error) {
        if (isMounted) {
          setQrDataUrl(null);
        }
        enqueueSnackbar('Failed to generate QR code. Use the secret instead.', { variant: 'warning' });
      }
    };
    buildQr();
    return () => {
      isMounted = false;
    };
  }, [setupData]);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const data = await authService.setupTwoFactor();
      setSetupData({ secret: data.secret, otpauth_url: data.otpauth_url });
      enqueueSnackbar('2FA setup started. Enter the code from your authenticator app.', { variant: 'info' });
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to start 2FA setup';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      await authService.verifyTwoFactor(code.trim());
      await refreshUser();
      setSetupData(null);
      setCode('');
      enqueueSnackbar('Two-factor authentication enabled.', { variant: 'success' });
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Invalid 2FA code';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await authService.disableTwoFactor(code.trim());
      await refreshUser();
      setCode('');
      enqueueSnackbar('Two-factor authentication disabled.', { variant: 'success' });
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to disable 2FA';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          LLM Setup (OpenAI)
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          LLM/API credentials are now managed centrally by system admins in <b>Admin → Credentials</b>.
          This Settings page no longer edits API keys.
        </Alert>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Two-Factor Authentication (2FA)
        </Typography>
        <Alert severity={user?.two_factor_enabled ? 'success' : 'warning'} sx={{ mt: 2 }}>
          {user?.two_factor_enabled
            ? '2FA is enabled on your account.'
            : '2FA is not enabled. We recommend enabling it for better security.'}
        </Alert>

        {!user?.two_factor_enabled && (
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSetup} disabled={loading}>
              {loading ? 'Starting...' : 'Enable 2FA'}
            </Button>
          </Box>
        )}

        {setupData && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              Secret: <b>{setupData.secret}</b>
              <IconButton
                aria-label="Copy secret"
                size="small"
                sx={{ ml: 1 }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(setupData.secret);
                    enqueueSnackbar('Secret copied to clipboard.', { variant: 'success' });
                  } catch (error) {
                    enqueueSnackbar('Failed to copy secret.', { variant: 'error' });
                  }
                }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Typography>
            {qrDataUrl && (
              <Box sx={{ mt: 2 }}>
                <img src={qrDataUrl} alt="2FA QR Code" style={{ maxWidth: 200 }} />
              </Box>
            )}
            <TextField
              margin="normal"
              fullWidth
              label="Enter 2FA Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button variant="contained" onClick={handleVerify} disabled={loading || !code.trim()}>
              Verify & Enable
            </Button>
          </Box>
        )}

        {user?.two_factor_enabled && (
          <Box sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Enter 2FA Code to Disable"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button variant="outlined" color="error" onClick={handleDisable} disabled={loading || !code.trim()}>
              Disable 2FA
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};